import { useEffect, useState } from "react";

import { isPresent } from "neetocist";
import useFuncDebounce from "neetocommons/react-utils/useFuncDebounce";
import useQueryParams from "neetocommons/react-utils/useQueryParams";
import useUpdateEffect from "neetocommons/react-utils/useUpdateEffect";
import { buildUrl } from "neetocommons/utils/general";
import { Search as NeetoUISearchIcon } from "neetoicons";
import { Input } from "neetoui";
import PropTypes from "prop-types";
import { mergeLeft } from "ramda";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import "./search.scss";
import { getInitialSearchValue } from "./utils";

const Search = ({
  value,
  debounceTime = 300,
  onSearch,
  onChange,
  enableUrlSync = false,
  searchParamName = "search_term",
  ...inputProps
}) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams({ toCamelCase: false });
  const history = useHistory();

  const [internalValue, setInternalValue] = useState(
    getInitialSearchValue({
      initialValue: value,
      enableUrlSync,
      searchParamName,
    })
  );
  const trimmedValue = value?.trim();

  useUpdateEffect(() => {
    if (!enableUrlSync || isPresent(queryParams?.[searchParamName])) return;

    setInternalValue("");
  }, [queryParams?.[searchParamName]]);

  const handleSearchQueryParams = trimmedValue => {
    if (!trimmedValue && !queryParams?.[searchParamName]) return;

    if (trimmedValue === queryParams?.[searchParamName]) return;

    let searchParams = {};
    const pathname = window.location.pathname;

    if (trimmedValue) searchParams = { [searchParamName]: trimmedValue };
    else delete queryParams?.[searchParamName];

    history.push(buildUrl(pathname, mergeLeft(searchParams, queryParams)));
  };

  const handleDebouncedSearchTermChange = useFuncDebounce(trimmedValue => {
    if (onSearch) onSearch(trimmedValue);
    else if (enableUrlSync) handleSearchQueryParams(trimmedValue);
  }, debounceTime);

  useUpdateEffect(() => {
    handleDebouncedSearchTermChange(trimmedValue);
  }, [trimmedValue]);

  if (value !== undefined && internalValue !== value) {
    setInternalValue(value);
  }

  const handleOnChange = e => {
    const trimmedValue = e.target.value?.trim();
    onChange?.(e);
    setInternalValue(e.target.value);

    if (internalValue?.trim() !== trimmedValue) {
      handleDebouncedSearchTermChange(trimmedValue);
    }
  };

  useEffect(() => handleDebouncedSearchTermChange.cancel, []);

  return (
    <Input
      className="neeto-molecules-search__wrapper"
      placeholder={t("neetoMolecules.common.actions.search")}
      prefix={<NeetoUISearchIcon />}
      type="search"
      value={internalValue}
      onChange={handleOnChange}
      {...inputProps}
    />
  );
};

Search.propTypes = {
  /**
   * value for the search (optional).
   */
  value: PropTypes.string,
  /**
   * Time in milliseconds to debounce the search input before triggering a search.
   */
  debounceTime: PropTypes.number,
  /**
   * To specify the function to be triggered when the value is updated (optional).
   */
  onChange: PropTypes.func,
  /**
   * Callback to handle debounced search value without updating the search params.
   */
  onSearch: PropTypes.func,
  /**
   * To control whether the search term needs to be set in the URLSearch.
   */
  enableUrlSync: PropTypes.bool,
  /**
   * value for the search param name.
   */
  searchParamName: PropTypes.string,
  /**
   * To override the properties of the Input component.
   */
  inputProps: PropTypes.object,
};

export default Search;
